import { useRef, useCallback } from "react";

/* Phone OS audio — tiny synthesized SFX (no assets, CEF-safe). Unlocks on the
   first touch inside the phone. Ringtones: pulse / synth / classic. */

export function usePhoneAudio() {
  const ctxRef = useRef(null);
  const ringTimer = useRef(null);

  const ensure = useCallback(() => {
    if (!ctxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      ctxRef.current = new Ctx();
    }
    if (ctxRef.current.state === "suspended") ctxRef.current.resume().catch(() => {});
    return ctxRef.current;
  }, []);

  const tone = useCallback((freq, { type = "sine", peak = 0.06, a = 0.005, d = 0.12, when = 0, slideTo = 0, slideT = 0.1 } = {}) => {
    const ctx = ensure(); if (!ctx) return;
    const o = ctx.createOscillator(); o.type = type;
    const t = ctx.currentTime + when;
    o.frequency.setValueAtTime(freq, t);
    if (slideTo) o.frequency.exponentialRampToValueAtTime(slideTo, t + slideT);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(Math.max(peak, 0.0001), t + a);
    g.gain.exponentialRampToValueAtTime(0.0001, t + a + d);
    o.connect(g); g.connect(ctx.destination);
    o.start(t); o.stop(t + a + d + 0.05);
  }, [ensure]);

  const bootChime = useCallback(() => { // phone power-on: rising triad + shimmer
    tone(523, { peak: 0.07, d: 0.3 });
    tone(659, { peak: 0.07, d: 0.3, when: 0.12 });
    tone(784, { peak: 0.09, d: 0.5, when: 0.24 });
    tone(1568, { peak: 0.03, d: 0.7, when: 0.34 });
  }, [tone]);

  const appOpen = useCallback(() => tone(880, { type: "triangle", peak: 0.045, d: 0.08, slideTo: 1175, slideT: 0.07 }), [tone]);
  const back = useCallback(() => tone(660, { type: "triangle", peak: 0.04, d: 0.08, slideTo: 440, slideT: 0.08 }), [tone]);
  const keyTick = useCallback(() => tone(1400 + Math.random() * 600, { type: "square", peak: 0.02, d: 0.03 }), [tone]);
  const sendSwoosh = useCallback(() => tone(500, { peak: 0.05, d: 0.2, slideTo: 1400, slideT: 0.18 }), [tone]);
  const receiveDing = useCallback(() => { tone(1047, { peak: 0.06, d: 0.15 }); tone(1568, { peak: 0.05, d: 0.3, when: 0.09 }); }, [tone]);
  const shutter = useCallback(() => { tone(2200, { type: "square", peak: 0.05, d: 0.02 }); tone(1100, { type: "square", peak: 0.05, d: 0.04, when: 0.05 }); }, [tone]);
  const success = useCallback(() => { tone(784, { peak: 0.06, d: 0.12 }); tone(1047, { peak: 0.06, d: 0.25, when: 0.1 }); }, [tone]);
  const error = useCallback(() => { tone(320, { type: "sawtooth", peak: 0.05, d: 0.16 }); tone(220, { type: "sawtooth", peak: 0.05, d: 0.3, when: 0.12 }); }, [tone]);
  const click = useCallback(() => tone(950, { type: "triangle", peak: 0.05, d: 0.06 }), [tone]);
  const tick = keyTick;
  const dialTone = useCallback(() => { tone(350, { peak: 0.04, d: 0.9 }); tone(440, { peak: 0.04, d: 0.9 }); }, [tone]);
  const ringback = useCallback(() => { tone(440, { peak: 0.05, d: 1.2 }); tone(480, { peak: 0.05, d: 1.2 }); }, [tone]);
  const dispatchChirp = useCallback(() => { // radio dispatch: two short squelch ticks + beep
    tone(1200, { type: "square", peak: 0.04, d: 0.03 });
    tone(900, { type: "square", peak: 0.04, d: 0.03, when: 0.06 });
    tone(1500, { peak: 0.05, d: 0.15, when: 0.14 });
  }, [tone]);

  const RING_PATTERNS = {
    pulse: [[880, 659], [880, 659]],
    synth: [[740, 988, 740, 1244], [988, 740]],
    classic: [[1047, 784, 1047, 784], [1319, 1047, 1319, 1047]],
  };
  const playRing = useCallback((name = "pulse") => { // one ring cycle (caller loops it)
    (RING_PATTERNS[name] || RING_PATTERNS.pulse).forEach((seq, gi) => {
      seq.forEach((f, i) => tone(f, { peak: 0.06, d: 0.28, when: gi * 1.0 + i * 0.32 }));
    });
  }, [tone]);

  const startRing = useCallback((name = "pulse") => {
    stopRing();
    const cycleMs = name === "classic" ? 4200 : name === "synth" ? 3600 : 2600;
    playRing(name);
    ringTimer.current = setInterval(() => playRing(name), cycleMs);
  }, [playRing]);

  function stopRing() { clearInterval(ringTimer.current); ringTimer.current = null; }

  return { ensure, bootChime, appOpen, back, keyTick, sendSwoosh, receiveDing, shutter, success, error, click, tick, dialTone, ringback, dispatchChirp, playRing, startRing, stopRing };
}
